import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api, { isLimitError } from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { storage } from '../product/productStorage';
import { useProducts } from '../product/ProductContext';
import { CREATE_KINDS } from '../product/productUtils';
import { CopyButton } from './create/generatorUi';
import './links.css';

const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'X', 'Facebook', 'LinkedIn'];

const readSelectedHooks = () => {
    try {
        const saved = storage.getJson('selectedHooks', []);
        return Array.isArray(saved) ? saved.filter(hook => typeof hook === 'string' && hook.trim()) : [];
    } catch (e) {
        return [];
    }
};

const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const toText = (value) => (typeof value === 'string' ? value : '');
const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// The API's link, with every field the page reads in a safe shape; null if it can't be used
const normalizeLink = (raw) => {
    if (!isPlainObject(raw) || !raw.slug) return null;
    return {
        ...raw,
        slug: String(raw.slug),
        shortUrl: toText(raw.shortUrl),
        destination: toText(raw.destination),
        platform: toText(raw.platform),
        campaign: toText(raw.campaign),
        hook: toText(raw.hook),
        clicks: toNumber(raw.clicks),
        uniqueClicks: toNumber(raw.uniqueClicks),
        clicksLast7Days: toNumber(raw.clicksLast7Days),
        bySource: isPlainObject(raw.bySource) ? raw.bySource : {},
    };
};

const normalizeLinks = (list) => (Array.isArray(list) ? list.map(normalizeLink).filter(Boolean) : []);

const hostnameOf = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch (e) {
        return url || '—';
    }
};

const shortLabel = (url) => url.replace(/^https?:\/\//, '');

const topEntry = (counts) => {
    const entries = Object.entries(counts || {}).filter(([, count]) => toNumber(count) > 0);
    if (entries.length === 0) return '—';
    return entries.sort((a, b) => toNumber(b[1]) - toNumber(a[1]))[0][0];
};

// Groups links by a field and ranks groups by average clicks per link, so a
// platform with many posts doesn't win just by volume
const rankBy = (links, field) => {
    const groups = {};
    links.filter(link => link[field]).forEach(link => {
        const group = groups[link[field]] || (groups[link[field]] = { name: link[field], links: 0, clicks: 0 });
        group.links += 1;
        group.clicks += link.clicks;
    });
    return Object.values(groups)
        .map(group => ({ ...group, average: group.clicks / group.links }))
        .sort((a, b) => b.average - a.average || b.clicks - a.clicks);
};

// "yourstore.com/x" is taken as https://yourstore.com/x; anything else must be a full http(s) URL
const toDestination = (value) => {
    const text = value.trim();
    const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
    try {
        const url = new URL(withScheme);
        return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.') ? url.toString() : null;
    } catch (e) {
        return null;
    }
};

const plural = (count, word) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

const Stat = ({ label, value, detail }) => (
    <div className="ls-card ls-links-stat">
        <div className="ls-metric-label">{label}</div>
        <div className="ls-links-stat-value">{value}</div>
        {/* Always there, so the tiles are the same height with or without a detail */}
        <div className="ls-links-stat-detail">{detail || ' '}</div>
    </div>
);

// The short link as a copy button; turns into a check for a moment after copying
const CopyLink = ({ link, isCopied, onCopy }) => {
    if (!link.shortUrl) return <span className="ls-muted">—</span>;
    const label = shortLabel(link.shortUrl);
    return (
        <button
            type="button"
            className={`ls-btn ls-btn-pill ls-links-copy${isCopied ? ' is-on' : ''}`}
            onClick={() => onCopy(link)}
            aria-label={isCopied ? `Copied ${label}` : `Copy ${label}`}
            title={link.shortUrl}
        >
            <Icon name={isCopied ? 'check' : 'content_copy'} size={14} />
            <span className="ls-links-copy-url">{label}</span>
        </button>
    );
};

const ConfirmDialog = ({ title, children, confirmLabel, isBusy, error, onConfirm, onCancel }) => {
    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && !isBusy && onCancel();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isBusy, onCancel]);

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !isBusy && onCancel()}>
            <div
                className="ls-modal ls-links-dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="ls-links-dialog-title"
                aria-describedby="ls-links-dialog-body"
            >
                <h2 id="ls-links-dialog-title" className="ls-links-dialog-title">{title}</h2>
                <div id="ls-links-dialog-body" className="ls-links-dialog-body">{children}</div>
                {error && <Alert severity="error">{error}</Alert>}
                <div className="ls-links-dialog-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={onCancel} disabled={isBusy} autoFocus>
                        Cancel
                    </button>
                    <button type="button" className="ls-btn ls-links-danger" onClick={onConfirm} disabled={isBusy}>
                        {isBusy ? <span className="ls-spinner" /> : <Icon name="delete" size={18} />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

const SKELETON_ROWS = [0, 1, 2];

const LinksScreen = () => {
    const { currentProduct, icps: productIcps } = useProducts();
    const icps = useMemo(() => (Array.isArray(productIcps) ? productIcps.filter(icp => icp && icp.id) : []), [productIcps]);
    const [links, setLinks] = useState([]);
    const [destination, setDestination] = useState('');
    const [platform, setPlatform] = useState('TikTok');
    const [campaign, setCampaign] = useState('');
    // Links carry the ICP and content type so the ICP pages can show what works for whom
    const [icpId, setIcpId] = useState(() => icps.find(icp => icp.isPrimary)?.id || '');
    const [contentType, setContentType] = useState(CREATE_KINDS.social.contentType);
    const [hook, setHook] = useState(() => readSelectedHooks().slice(-1)[0] || '');
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [formError, setFormError] = useState(null); // { field, message }
    const [created, setCreated] = useState(null); // { link, copied }
    const [copiedSlug, setCopiedSlug] = useState(null);
    const [copyError, setCopyError] = useState('');
    const [newSlug, setNewSlug] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const selectedHooks = useMemo(readSelectedHooks, []);
    const copyTimer = useRef(null);
    const destinationRef = useRef(null);
    const campaignRef = useRef(null);

    useEffect(() => () => clearTimeout(copyTimer.current), []);

    const loadLinks = useCallback(async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            setLinks(normalizeLinks(await api.listTrackedLinks()));
        } catch (e) {
            setLoadError("Couldn't load your links. Check your connection and try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLinks();
    }, [loadLinks]);

    // quiet: after creating a link the copy is a convenience, so a blocked clipboard isn't an error
    const handleCopy = async (link, { quiet = false } = {}) => {
        if (!link.shortUrl) return false;
        try {
            await navigator.clipboard.writeText(link.shortUrl);
            setCopiedSlug(link.slug);
            setCopyError('');
            clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopiedSlug(null), 2000);
            return true;
        } catch (e) {
            if (!quiet) setCopyError("Couldn't copy the link. Your browser blocked the clipboard; select the link and copy it by hand.");
            return false;
        }
    };

    const failField = (field, message) => {
        setFormError({ field, message });
        (field === 'destination' ? destinationRef : campaignRef).current?.focus();
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        if (isCreating) return;
        if (!destination.trim()) {
            failField('destination', 'Enter the page the link should lead to, e.g. https://yourstore.com/product.');
            return;
        }
        const url = toDestination(destination);
        if (!url) {
            failField('destination', "That doesn't look like a web address. Use a full URL like https://yourstore.com/product.");
            return;
        }
        if (!campaign.trim()) {
            failField('campaign', 'Give the post a name so you can tell its clicks apart, e.g. Launch video #1.');
            return;
        }

        setIsCreating(true);
        setFormError(null);
        setCreated(null);
        try {
            const link = normalizeLink(await api.createTrackedLink({
                destination: url,
                platform,
                campaign: campaign.trim(),
                hook: hook.trim(),
                icpId: icpId || undefined,
                contentType,
            }));
            if (!link) {
                // Created, but the answer was unusable: the list has the real link
                loadLinks();
                setFormError({ message: 'The link was created, but we couldn\'t show it here. Copy it from "Your links" below.' });
                return;
            }
            setLinks(current => [link, ...current.filter(item => item.slug !== link.slug)]);
            setNewSlug(link.slug);
            setCampaign('');
            setCreated({ link, copied: await handleCopy(link, { quiet: true }) });
        } catch (e) {
            // The upgrade prompt explains plan limits
            if (!isLimitError(e)) setFormError({ message: e.response?.data?.error || "Couldn't create the link. Please try again." });
        } finally {
            setIsCreating(false);
        }
    };

    const closeDelete = useCallback(() => {
        setPendingDelete(null);
        setDeleteError('');
    }, []);

    const handleDelete = async () => {
        const { slug } = pendingDelete;
        setIsDeleting(true);
        setDeleteError('');
        try {
            await api.deleteTrackedLink(slug);
            setLinks(current => current.filter(link => link.slug !== slug));
            if (created?.link.slug === slug) setCreated(null);
            closeDelete();
        } catch (e) {
            setDeleteError("Couldn't delete the link. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const insights = useMemo(() => {
        const sum = (field) => links.reduce((total, link) => total + link[field], 0);
        return {
            clicks: sum('clicks'),
            uniqueClicks: sum('uniqueClicks'),
            clicksLast7Days: sum('clicksLast7Days'),
            platforms: rankBy(links, 'platform'),
            hooks: rankBy(links, 'hook').slice(0, 5),
        };
    }, [links]);

    const maxPlatformAverage = Math.max(1, ...insights.platforms.map(p => p.average));
    const bestPlatform = insights.platforms[0]?.clicks > 0 ? insights.platforms[0] : null;
    const bestHook = insights.hooks[0]?.clicks > 0 ? insights.hooks[0] : null;
    // First load only: a refresh keeps the current numbers on screen
    const isFirstLoad = isLoading && links.length === 0;
    const hasNoData = Boolean(loadError) && links.length === 0;
    const statValue = (value) => {
        if (isFirstLoad) return <span className="ls-skeleton-text" style={{ width: '2.5em' }} />;
        return hasNoData ? '—' : value;
    };
    const focusForm = () => {
        destinationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        destinationRef.current?.focus({ preventScroll: true });
    };
    const fieldInvalid = (field) => (formError?.field === field ? true : undefined);

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Measure{currentProduct?.name ? ` · ${currentProduct.name}` : ''}</div>
                    <h1 className="ls-page-title">Tracked links</h1>
                </div>
                <div className="ls-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={loadLinks} disabled={isLoading}>
                        {isLoading ? <span className="ls-spinner" /> : <Icon name="refresh" size={18} />}
                        Refresh
                    </button>
                </div>
            </div>

            <div className="ls-links-stats">
                <Stat label="Total clicks" value={statValue(insights.clicks.toLocaleString())} />
                <Stat label="Unique visitors" value={statValue(insights.uniqueClicks.toLocaleString())} />
                <Stat label="Clicks, last 7 days" value={statValue(insights.clicksLast7Days.toLocaleString())} />
                <Stat
                    label="Best platform"
                    value={statValue(bestPlatform ? bestPlatform.name : '—')}
                    detail={!isFirstLoad && bestPlatform && `${bestPlatform.average.toFixed(1)} clicks per post`}
                />
            </div>

            <div className="ls-two-col">
                <form className="ls-card ls-links-form" onSubmit={handleCreate} noValidate>
                    <div>
                        <div className="ls-card-title">New link</div>
                        <div className="ls-card-sub" style={{ marginTop: 2 }}>
                            Put a tracked link in each post. Every click is counted and tied to the post, platform and hook,
                            so you can see what actually drives traffic.
                        </div>
                    </div>
                    <div className="ls-form-grid">
                        <label className="ls-field is-wide">
                            <span className="ls-label">Destination URL</span>
                            <input
                                ref={destinationRef}
                                className="ls-input"
                                type="url"
                                inputMode="url"
                                value={destination}
                                onChange={(e) => setDestination(e.target.value)}
                                placeholder="https://yourstore.com/product"
                                aria-invalid={fieldInvalid('destination')}
                                aria-describedby="ls-links-destination-hint"
                                required
                            />
                            <span id="ls-links-destination-hint" className="ls-hint">Where the link leads. UTM tags are added for you.</span>
                        </label>
                        <label className="ls-field">
                            <span className="ls-label">Post name</span>
                            <input
                                ref={campaignRef}
                                className="ls-input"
                                value={campaign}
                                onChange={(e) => setCampaign(e.target.value)}
                                placeholder="e.g. Launch video #1"
                                aria-invalid={fieldInvalid('campaign')}
                                required
                            />
                        </label>
                        <label className="ls-field">
                            <span className="ls-label">Platform</span>
                            <select className="ls-select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                                {PLATFORMS.map(name => <option key={name} value={name}>{name}</option>)}
                            </select>
                        </label>
                        <label className="ls-field is-wide">
                            <span className="ls-label">Hook used in the post (optional)</span>
                            <input
                                className="ls-input"
                                list="ls-links-selected-hooks"
                                value={hook}
                                onChange={(e) => setHook(e.target.value)}
                                placeholder="e.g. Stop wasting your mornings on cold coffee"
                            />
                            <datalist id="ls-links-selected-hooks">
                                {selectedHooks.map(h => <option key={h} value={h} />)}
                            </datalist>
                        </label>
                        <label className="ls-field">
                            <span className="ls-label">Customer profile</span>
                            <select className="ls-select" value={icpId} onChange={(e) => setIcpId(e.target.value)}>
                                <option value="">No specific ICP</option>
                                {icps.map(icp => <option key={icp.id} value={icp.id}>For {icp.name || 'unnamed ICP'}</option>)}
                            </select>
                        </label>
                        <label className="ls-field">
                            <span className="ls-label">Content type</span>
                            <select className="ls-select" value={contentType} onChange={(e) => setContentType(e.target.value)}>
                                {Object.values(CREATE_KINDS).map(kind => (
                                    <option key={kind.contentType} value={kind.contentType}>{kind.contentType}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {formError && <Alert severity="error">{formError.message}</Alert>}
                    {created && (
                        <Alert
                            severity="success"
                            onClose={() => setCreated(null)}
                            action={<CopyButton text={created.link.shortUrl} label="Copy again" />}
                        >
                            {created.copied ? 'Link created and copied. ' : 'Link created. '}
                            Paste <strong className="ls-links-break">{shortLabel(created.link.shortUrl)}</strong> into your post.
                        </Alert>
                    )}
                    <button type="submit" className="ls-btn ls-btn-primary is-block" disabled={isCreating}>
                        {isCreating ? <span className="ls-spinner" /> : <Icon name="add_link" size={18} />}
                        {isCreating ? 'Creating…' : 'Create & copy link'}
                    </button>
                </form>

                <div className="ls-col">
                    <div className="ls-card">
                        <div className="ls-card-title">Clicks per post by platform</div>
                        {isFirstLoad ? (
                            <div className="ls-links-bars" aria-hidden="true">
                                {SKELETON_ROWS.map(i => <div key={i} className="ls-skeleton" style={{ height: 20 }} />)}
                            </div>
                        ) : insights.platforms.length === 0 ? (
                            <p className="ls-empty" style={{ margin: 0 }}>
                                Once your links get clicks, platforms are compared here by clicks per post, not just volume.
                            </p>
                        ) : (
                            <div className="ls-links-bars ls-stagger">
                                {insights.platforms.map((p, index) => (
                                    <div key={p.name} className="ls-links-bar" style={{ '--i': index }}>
                                        <span className="ls-links-bar-label" title={p.name}>{p.name}</span>
                                        <div className="ls-links-bar-track" aria-hidden="true">
                                            <div className="ls-links-bar-fill" style={{ width: `${(p.average / maxPlatformAverage) * 100}%` }} />
                                        </div>
                                        <span className="ls-links-bar-value">
                                            {p.average.toFixed(1)} <span className="ls-muted">· {plural(p.links, 'post')}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ls-card">
                        <div className="ls-card-title">Top hooks</div>
                        {isFirstLoad ? (
                            <div className="ls-links-bars" aria-hidden="true">
                                {SKELETON_ROWS.map(i => <div key={i} className="ls-skeleton" style={{ height: 38 }} />)}
                            </div>
                        ) : insights.hooks.length === 0 ? (
                            <p className="ls-empty" style={{ margin: 0 }}>
                                Add the hook when you create a link to see which opening lines bring the most clicks.
                            </p>
                        ) : (
                            <ol className="ls-links-hooks ls-stagger">
                                {insights.hooks.map((h, index) => (
                                    <li key={h.name} className="ls-links-hook" style={{ '--i': index }}>
                                        <span className="ls-links-hook-rank">{index + 1}</span>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="ls-links-hook-text">“{h.name}”</div>
                                            <div className="ls-row-sub">
                                                {h.average.toFixed(1)} clicks per post · {plural(h.links, 'post')}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                        {bestHook && (
                            <div className="ls-card-footer">Tip: generate more hooks like your best one in Social posts.</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="ls-card">
                <div className="ls-card-header">
                    <span className="ls-card-title">Your links</span>
                    {links.length > 0 && <span className="ls-card-sub">{plural(links.length, 'link')}</span>}
                </div>

                {loadError && (
                    <Alert
                        severity="error"
                        action={(
                            <button type="button" className="ls-btn ls-btn-secondary ls-links-alert-btn" onClick={loadLinks} disabled={isLoading}>
                                {isLoading ? <span className="ls-spinner" /> : null}Try again
                            </button>
                        )}
                    >
                        {loadError}
                    </Alert>
                )}
                {copyError && <Alert severity="error" onClose={() => setCopyError('')}>{copyError}</Alert>}

                {isFirstLoad ? (
                    <div className="ls-table-wrap" aria-hidden="true">
                        <table className="ls-table">
                            <tbody>
                                {SKELETON_ROWS.map(i => (
                                    <tr key={i}>
                                        <td><div className="ls-skeleton" style={{ height: 36, width: '70%' }} /></td>
                                        <td><span className="ls-skeleton-text" style={{ width: 64 }} /></td>
                                        <td className="ls-links-hide-mobile"><span className="ls-skeleton-text" style={{ width: 120 }} /></td>
                                        <td className="is-num"><span className="ls-skeleton-text" style={{ width: 32 }} /></td>
                                        <td className="ls-links-hide-mobile"><span className="ls-skeleton-text" style={{ width: 140 }} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : links.length === 0 ? (
                    !loadError && (
                        <div className="ls-placeholder ls-links-empty">
                            <Icon name="link" />
                            <p className="ls-empty" style={{ margin: 0, maxWidth: 460 }}>
                                No tracked links yet. Create one, put it in your post's caption or bio, and every click shows up
                                here with the post, platform and hook that drove it.
                            </p>
                            <button type="button" className="ls-btn ls-btn-secondary" onClick={focusForm}>
                                <Icon name="add_link" size={18} />Create your first link
                            </button>
                        </div>
                    )
                ) : (
                    <>
                        <div className="ls-table-wrap ls-links-table">
                            <table className="ls-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Post</th>
                                        <th scope="col">Platform</th>
                                        <th scope="col">Hook</th>
                                        <th scope="col" className="is-num">Clicks</th>
                                        <th scope="col" className="is-num">Unique</th>
                                        <th scope="col" className="is-num">7 days</th>
                                        <th scope="col">Top source</th>
                                        <th scope="col">Link</th>
                                        <th scope="col"><span className="ls-links-sr">Actions</span></th>
                                    </tr>
                                </thead>
                                <tbody className="ls-stagger">
                                    {links.map((link, index) => (
                                        <tr key={link.slug} className={link.slug === newSlug ? 'ls-links-new' : undefined} style={{ '--i': index }}>
                                            <td>
                                                <div className="ls-links-post" title={link.campaign}>{link.campaign || 'Untitled post'}</div>
                                                <div className="ls-links-sub" title={link.destination}>{hostnameOf(link.destination)}</div>
                                            </td>
                                            <td>{link.platform ? <span className="ls-chip">{link.platform}</span> : <span className="ls-muted">—</span>}</td>
                                            <td className="ls-links-hook-cell" title={link.hook}>
                                                {link.hook ? <span className="ls-links-clamp">{link.hook}</span> : <span className="ls-muted">—</span>}
                                            </td>
                                            <td className="is-num ls-links-strong">{link.clicks.toLocaleString()}</td>
                                            <td className="is-num">{link.uniqueClicks.toLocaleString()}</td>
                                            <td className="is-num">{link.clicksLast7Days.toLocaleString()}</td>
                                            <td className="ls-links-source" title={topEntry(link.bySource)}>{topEntry(link.bySource)}</td>
                                            <td><CopyLink link={link} isCopied={copiedSlug === link.slug} onCopy={handleCopy} /></td>
                                            <td className="ls-links-action">
                                                <button
                                                    type="button"
                                                    className="ls-btn ls-btn-icon"
                                                    onClick={() => setPendingDelete(link)}
                                                    aria-label={`Delete link for ${link.campaign || 'untitled post'}`}
                                                    title="Delete link"
                                                >
                                                    <Icon name="delete" size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="ls-results ls-links-cards ls-stagger">
                            {links.map((link, index) => (
                                <div key={link.slug} className={`ls-result${link.slug === newSlug ? ' is-selected' : ''}`} style={{ '--i': index }}>
                                    <div className="ls-result-head">
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div className="ls-row-title">{link.campaign || 'Untitled post'}</div>
                                            <div className="ls-row-sub">
                                                {[link.platform, hostnameOf(link.destination)].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="ls-btn ls-btn-icon"
                                            onClick={() => setPendingDelete(link)}
                                            aria-label={`Delete link for ${link.campaign || 'untitled post'}`}
                                        >
                                            <Icon name="delete" size={18} />
                                        </button>
                                    </div>
                                    {link.hook && <div className="ls-links-card-hook">“{link.hook}”</div>}
                                    <div className="ls-links-card-stats">
                                        <span><b>{link.clicks.toLocaleString()}</b> clicks</span>
                                        <span><b>{link.uniqueClicks.toLocaleString()}</b> unique</span>
                                        <span><b>{link.clicksLast7Days.toLocaleString()}</b> this week</span>
                                        {topEntry(link.bySource) !== '—' && <span>from {topEntry(link.bySource)}</span>}
                                    </div>
                                    <div><CopyLink link={link} isCopied={copiedSlug === link.slug} onCopy={handleCopy} /></div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <span className="ls-links-sr" aria-live="polite">{copiedSlug ? 'Link copied' : ''}</span>

                {pendingDelete && (
                    <ConfirmDialog
                        title="Delete this link?"
                        confirmLabel="Delete link"
                        isBusy={isDeleting}
                        error={deleteError}
                        onConfirm={handleDelete}
                        onCancel={closeDelete}
                    >
                        <strong>{pendingDelete.campaign || 'This link'}</strong> stops redirecting
                        {pendingDelete.clicks > 0 ? ` and its ${plural(pendingDelete.clicks, 'click')} ${pendingDelete.clicks === 1 ? 'is' : 'are'} deleted` : ''}.
                        Posts that use it will lead nowhere. This can't be undone.
                    </ConfirmDialog>
                )}
            </div>
        </>
    );
};

export default LinksScreen;
